import * as React from "react";
import { LinkProps } from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { useOfferModeWithFsecure } from "@nju/data-access-portal-offers/src/utils";
import { useOrchestrators } from "@nju/data-access-portal-orchestrator";
import { useUser } from "@nju/data-access-portal-user";
import { portalRoutes } from "@nju/routes";
import { Back } from "@nju/ui";
import { BlikPaymentBanner } from "./components/BlikPaymentBanner";
import { CreditCardPaymentBanner } from "./components/CreditCardPaymentBanner";
import { DashboardPaymentCTABanner } from "./components/DashboardPaymentCTABanner";
import { NoCardBanner } from "./components/NoCardBanner";
import { mainPaymentPageMachine } from "./machines/mainPaymentPage.machine";

export function DashboardMainPaymentPage() {
  const { availablePaymentMethods, hasCreditCard, primaryPaymentMethod } =
    useUser();
  const router = useRouter();
  const mode = useOfferModeWithFsecure();
  const { ftth, ftthReactivation, fsecureReactivation } = useOrchestrators();
  const memberOrchestrator = useMemberOrchestrator();
  const state = ftth?.state;
  const userId = ftth?.userId;
  const params = portalRoutes.mojeKonto.platnosci.dodajSrodki
    .input(router.query)
    .unwrap();

  const isSubmitted =
    state &&
    [
      "ORDER_COMMITTED",
      "ORDER_FULFILLMENT",
      "ORDER_PAYMENT",
      "ORDER_FINISHING",
    ].includes(state);

  const isReactivationProcess = portalRoutes.mojeKonto.platnosci.index.hasParam(
    router.query,
    "procesReaktywacji",
  );

  const changePaymentMethodFromSummary =
    portalRoutes.mojeKonto.platnosci.index.hasParam(
      router.query,
      "zmianaMetodyPlatnosci",
    );

  const isCreditCardPrimary =
    primaryPaymentMethod?.methodType === "CREDIT_CARD";

  const ftthBackButtonRedirect = ftthReactivation
    ? portalRoutes.mojeKonto.mojeUslugi.reaktywacja.index.get({
        mode: "ftth",
      })
    : portalRoutes.podsumowanie.get({ mode: "ftth" });

  const mobileBackButtonRedirect = memberOrchestrator.mobileReactivation
    ? portalRoutes.mojeKonto.mojeUslugi.reaktywacja.index.get({
        mode: "mobile",
        ...(params?.userId && { userId: params.userId }),
      })
    : portalRoutes.podsumowanie.get({ mode: "mobile" });

  const postpaidBackButtonRedirect = memberOrchestrator.mobileReactivation
    ? portalRoutes.mojeKonto.mojeUslugi.reaktywacja.index.get({
        mode: "postpaid",
        ...(params?.userId && { userId: params.userId }),
      })
    : portalRoutes.podsumowanie.get({ mode: "postpaid" });

  const fSecureBackButtonRedirect = fsecureReactivation
    ? portalRoutes.mojeKonto.mojeUslugi.reaktywacja.index.get({
        mode: "fsecure",
      })
    : portalRoutes.podsumowanie.get({ mode: "fsecure" });

  const backButtonRedirectMap = {
    ftth: ftthBackButtonRedirect,
    mobile: mobileBackButtonRedirect,
    postpaid: postpaidBackButtonRedirect,
    fsecure: fSecureBackButtonRedirect,
  };

  const backButtonRedirect = backButtonRedirectMap[mode];

  const [current, send] = useMachine(mainPaymentPageMachine, {
    context: {
      userId,
      isCreditCardPrimary,
    },
    actions: {
      onHandleReloadPage: () => {
        return window.location.reload();
      },
    },
  });

  const handleCancelPaymentSwitch = React.useCallback(() => {
    send({ type: "DELETE_PAYMENT" });
  }, [send]);

  const { context, matches } = current;

  const renderAvailablePaymentMethods = () => {
    return availablePaymentMethods.map((paymentMethod) => {
      const isActive = paymentMethod.details.mode === "PRIMARY";
      const changePaymentMethodUrl: LinkProps["href"] | undefined = !isActive
        ? portalRoutes.mojeKonto.platnosci.zmianaMetody.get({
            id: paymentMethod.id,
            type: paymentMethod.methodType,
            ...(isReactivationProcess && { procesReaktywacji: "1", mode }),
            ...(changePaymentMethodFromSummary && {
              zmianaMetodyPlatnosci: "1",
              mode,
            }),
            ...(params?.userId && { userId: params.userId }),
          })
        : undefined;

      switch (paymentMethod.methodType) {
        case "DIGITAL_WALLET":
          return (
            <BlikPaymentBanner
              amount={paymentMethod.details.amount}
              changePaymentMethodUrl={changePaymentMethodUrl}
              isActive={isActive}
              key={paymentMethod.id}
              userId={params?.userId}
            />
          );
        case "CREDIT_CARD":
          return (
            <CreditCardPaymentBanner
              cardId={paymentMethod.id}
              cardNumber={paymentMethod.details.maskedCardNumber}
              changePaymentMethodUrl={changePaymentMethodUrl}
              isActive={isActive}
              key={paymentMethod.id}
              userId={params?.userId}
            />
          );
        default:
          return;
      }
    });
  };

  return (
    <>
      {changePaymentMethodFromSummary && !isSubmitted && (
        <div className="mt-[-20px]">
          <Back
            iconColor="accent"
            text="wróć do podsumowania"
            textTone="accent"
            type="LINK"
            url={backButtonRedirect}
          />
        </div>
      )}
      {context.isPending && (
        <DashboardPaymentCTABanner
          handleCancelPaymentSwitch={handleCancelPaymentSwitch}
          isLoading={matches("deletePayment")}
        />
      )}
      <div className="grid gap-3 grid-cols-[1fr] from-tablet:gap-4 from-tablet:grid-cols-[1fr,1fr]">
        {renderAvailablePaymentMethods()}
        {!hasCreditCard && <NoCardBanner />}
      </div>
    </>
  );
}
